// PWA Notification Handler
// This component handles service worker events and shows notifications using the toast system

import { useEffect } from 'react';
import { useNotifications } from './NotificationProvider';
import swManager from '../../utils/serviceWorker.js';

/**
 * PWA Notification Handler Component
 * Connects service worker events to the notification toast system
 */
export function PWANotificationHandler() {
  const { showInfo, showSuccess, showWarning, showModal } = useNotifications();

  useEffect(() => {
    // Handle service worker update available
    const handleUpdateAvailable = async () => {
      // Show a modal asking user if they want to update now
      const shouldUpdate = await showModal({
        type: 'confirm',
        title: 'App Update Available',
        message:
          '🔄 A new version of the app is available. Would you like to update now? The app will reload automatically.',
        confirmText: 'Update Now',
        cancelText: 'Later',
      });

      if (shouldUpdate) {
        // Apply update immediately
        swManager.skipWaiting();
        showInfo('Updating app...', 2000);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        // Show info toast for later
        showInfo('Update will be applied on next app reload.', 5000);
      }
    };

    // Handle offline status
    const handleOffline = () => {
      showWarning(
        "📱 You're now offline. You can still view cached content, but some features may be limited.",
        6000 // Show for 6 seconds
      );
    };

    // Handle online status
    const handleOnline = () => {
      showSuccess(
        "🌐 You're back online! All features are now available.",
        4000 // Show for 4 seconds
      );
    };

    // Handle service worker registration success
    const handleRegistered = () => {
      if (import.meta.env.DEV) {
        // Only show in development for debugging
        showSuccess('Service Worker registered successfully', 3000);
      }
    };

    // Add event listeners
    swManager.on('onUpdateAvailable', handleUpdateAvailable);
    swManager.on('onOffline', handleOffline);
    swManager.on('onOnline', handleOnline);
    swManager.on('onRegistered', handleRegistered);

    // Cleanup on unmount
    return () => {
      swManager.off('onUpdateAvailable', handleUpdateAvailable);
      swManager.off('onOffline', handleOffline);
      swManager.off('onOnline', handleOnline);
      swManager.off('onRegistered', handleRegistered);
    };
  }, [showInfo, showSuccess, showWarning, showModal]);

  // This component doesn't render anything
  return null;
}
